import { Component } from '@theme/component';
import { LoadManager } from '@theme/custom-collection-utility';
import { hasValidSubscriptionSurveyResultForSession } from '@theme/thuysvers-cart-selling-plan-utils';

const Utils = /** @type {NonNullable<typeof window.ThuysversSurveyFormUtils>} */ (window.ThuysversSurveyFormUtils);
const Klaviyo = /** @type {NonNullable<typeof window.ThuysversSurveyFormKlaviyo>} */ (window.ThuysversSurveyFormKlaviyo);
const EMAIL_REGEX = Utils.EMAIL_REGEX;
const STORAGE_KEY_RECOMMENDATIONS = 'recommendations';
const STORAGE_KEY_SURVEY_STARTED_EMAIL = 'thuysvers_subscription_survey_started_email';
const STORAGE_KEY_SURVEY_COMPLETED_EMAIL = 'thuysvers_subscription_survey_completed_email';
const STORAGE_KEY_SUBSCRIPTION_SURVEY_STATE = 'thuysvers_subscription_survey_state';
const STORAGE_KEY_SUBSCRIPTION_SURVEY_RESULT = 'subscription_survey_result';
const STORAGE_KEY_ACTIVE_SUBSCRIPTION = 'tv_has_active_subscription';
const STORAGE_KEY_OPEN_SURVEY_INTENT = 'thuysvers_open_survey_intent';
const EVENT_RECOMMENDATIONS_UPDATED = 'ecomm-recommendations-updated';

/** data-fieldType values for step routing */
const FIELD_TYPE = {
  SUBMIT: 'Submit',
  EMAIL: 'Email',
  SURVEY_STATUS: 'SurveyStatus',
  FIRMHOUSE_SUBSCRIPTION: 'FirmhouseSubscriptionStatus',
};

/** data-answerKey value for add-ons step (subscription uses "soup") */
const ANSWER_KEY_ADDONS = 'soup';
const ANSWER_KEY_PROFILE_COUNT = 'profilecount';

/**
 * A custom element that manages the Thuysvers Survey Forms.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} closeSurveyButton - The close survey button element
 * @property {HTMLButtonElement} startSurveyButton - The start survey button element
 * @property {HTMLButtonElement} prevSurveyButton - The previous survey button element
 * @property {HTMLButtonElement} nextSurveyButton - The next survey button element
 * @property {HTMLElement[]} surveyStep[] - The survey step elements
 * @property {HTMLElement} surveyFooterContentWrapper - The survey footer content wrapper element
 * @property {HTMLButtonElement[]} qtyBtnInc[] - Quantity increment buttons
 * @property {HTMLButtonElement[]} qtyBtnDec[] - Quantity decrement buttons
 * @property {HTMLInputElement[]} qtyInput[] - Quantity inputs
 * @property {HTMLElement} surveySubmitWrapper - The survey submit wrapper element
 * @extends {Component<Refs>}
 */
export class ThuysversSubscriptionSurveyComponent extends Component {
  /** @type {HTMLFormElement | null} */
  formEl = null;

  /** @type {boolean} */
  isSubmitting = false;

  /** @type {boolean} */
  isCheckingStatus = false;

  /** @type {number} */
  profileCountSelected = 1;

  /** @type {Map<HTMLElement, (event: Event) => void>} */
  qtyBtnHandlers = new Map();

  /** @type {Map<HTMLInputElement, (event: Event) => void>} */
  qtyInputHandlers = new Map();

  /** @type {number | null} */
  saveStateTimer = null;

  // ----- Lifecycle -----

  connectedCallback() {
    try {
      // Capture openSurvey intent before URL gets cleaned (it's often removed after a few seconds)
      this.#captureOpenSurveyIntent();
      super.connectedCallback();
      this.refs.closeSurveyButton?.addEventListener('click', this.#closeSurveyForm);
      this.refs.startSurveyButton?.addEventListener('click', this.#startSurvey);
      this.refs.prevSurveyButton?.addEventListener('click', this.#prevSurveyStep);
      this.refs.nextSurveyButton?.addEventListener('click', this.#nextSurveyStep);

      this.formEl = this.querySelector('form[data-thuysvers-survey]');
      this.formEl?.addEventListener('input', this.#onFormInputOrChange, true);
      this.formEl?.addEventListener('change', this.#onFormInputOrChange, true);
      this.formEl?.addEventListener('keydown', this.#onFormKeyDown, true);

      this.bindQtyControlsStatic();

      // If URL contains subscription survey email, hydrate session storage FIRST (before init)
      // so routeInitialSurveyState finds the data and can skip showing the survey.
      void this.#initWithUrlHydrationAndSubscriptionCheck();
    } catch (error) {
      console.error('[THUYSVERS-SURVEY-FORMS] Error in connectedCallback', error);
      throw error;
    }
  }

  /**
   * Runs URL hydration (subscriptionsurvey_email) first when present, then subscription check.
   * This ensures session storage is populated before routeInitialSurveyState reads it.
   */
  async #initWithUrlHydrationAndSubscriptionCheck() {
    const search = window.location?.search || '';
    if (search) {
      const params = new URLSearchParams(search);
      const raw = params.get('subscriptionsurvey_email');
      const email = raw ? String(raw).trim().replace(/\s+/g, '+') : '';
      if (email && EMAIL_REGEX.test(email)) {
        await this.#getSubscriptionSurveyEmailFromUrl();
      }
    }
    await this.#initWithSubscriptionCheck();
  }

  /**
   * On page load: redirect if active subscription (logged-in or guest with flag from same session).
   * Otherwise run the normal initial state routing.
   */
  async #initWithSubscriptionCheck() {
    try {
      // Guest with active subscription (from same session): redirect immediately without showing survey.
      if (this.#redirectIfGuestHasActiveSubscription()) return;
      // Logged-in customer: check status API and redirect if active subscription.
      const redirected = await this.#redirectIfActiveSubscription();
      if (redirected) return;
      this.routeInitialSurveyState();
      this.updateNavState();
    } catch {
      this.routeInitialSurveyState();
      this.updateNavState();
    }
  }

  /**
   * If sessionStorage has the active-subscription flag (e.g. user hit back after being redirected),
   * redirect immediately so they cannot stay on the page.
   * @returns {boolean}
   */
  #redirectIfGuestHasActiveSubscription() {
    try {
      if (sessionStorage.getItem(STORAGE_KEY_ACTIVE_SUBSCRIPTION) !== '1') return false;
      const url = this.formEl?.getAttribute('data-firmhouse-redirect-url');
      if (!url) return false;
      window.location.replace(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * For logged-in customers: check status API. If has active subscription, show the
   * firmhouse-subscription-status step instead of redirecting. User sees the message
   * and clicks the button to redirect.
   * @returns {Promise<boolean>} True if we handled the case (show step or redirect)
   */
  async #redirectIfActiveSubscription() {
    const email = this.getCustomerEmailForStatusCheck();
    if (!email || !EMAIL_REGEX.test(email)) return false;

    const status = await this.fetchSurveyStatus(email);
    if (!status.hasActivatedFirmhouseSubscription || !status.hasOngoingFirmhouseSubscription) return false;

    // Set flag so returning to this URL (e.g. new tab) still triggers redirect off the survey.
    try {
      sessionStorage.setItem(STORAGE_KEY_ACTIVE_SUBSCRIPTION, '1');
    } catch {
      // ignore
    }
    // Show the firmhouse-subscription-status step so the user sees the final step
    // ("Je hebt al een actief abonnement!"). They can then click the button to redirect.
    this.showSurveyForm();
    this.setFirmhouseSubscriptionStatusStepEnabled(true);
    const idx = this.getFirmhouseSubscriptionStatusStepIndex();
    if (idx >= 0) {
      this.setActiveStepIndex(idx);
      this.updateNavState();
    }
    return true;
  }

  /**
   * Returns logged-in customer email from form data attribute (for status check on page load).
   * @returns {string}
   */
  getCustomerEmailForStatusCheck() {
    const form = this.formEl;
    if (!form) return '';
    return String(form.getAttribute('data-customer-email') || '').trim();
  }

  /**
   * If the URL has `?subscriptionsurvey_email=...`, fetch the survey status and persist the
   * recommendations payload and subscription survey result to sessionStorage. This mimics the
   * Email-step behavior around the status check + payload persist.
   */
  #getSubscriptionSurveyEmailFromUrl = async () => {
    /** @type {boolean} */
    let loaderShown = false;
    try {
      const search = window.location?.search || '';
      if (!search) return;

      const params = new URLSearchParams(search);
      const raw = params.get('subscriptionsurvey_email');
      if (!raw) return;

      // URLSearchParams treats "+" as space; restore it for emails.
      const email = String(raw).trim().replace(/\s+/g, '+');
      if (!email || !EMAIL_REGEX.test(email)) return;

      LoadManager.addLoader();
      loaderShown = true;

      const status = await this.fetchSurveyStatus(email);
      const hasCompletedOrActive =
        status.hasTakenSurvey ||
        (status.hasActivatedFirmhouseSubscription && status.hasOngoingFirmhouseSubscription);

      if (hasCompletedOrActive) {
        if (status.payload) {
          this.persistRecommendationsPayload(status.payload);
        }
        if (status.subscriptionSurveyResult) {
          this.persistSubscriptionSurveyResult(status.subscriptionSurveyResult);
        }
      } else {
        // If the email has NOT taken the survey, ensure "Aanbevolen" isn't applied.
        const RECO_PARAM = 'filter.p.m.custom.product_recommendations';
        const url = new URL(window.location.href);
        if (url.searchParams.has(RECO_PARAM)) {
          url.searchParams.delete(RECO_PARAM);

          const facetsFormComponent = document.querySelector('#filters-drawer facets-form-component');
          if (
            facetsFormComponent &&
            typeof /** @type {any} */ (facetsFormComponent).updateFiltersByURLWithoutBrowserUpdate === 'function'
          ) {
            await /** @type {any} */ (facetsFormComponent).updateFiltersByURLWithoutBrowserUpdate(url.toString());
          } else if (
            facetsFormComponent &&
            typeof /** @type {any} */ (facetsFormComponent).updateFiltersByURL === 'function'
          ) {
            await /** @type {any} */ (facetsFormComponent).updateFiltersByURL(url.toString());
          } else {
            try {
              history.replaceState(history.state, '', url.toString());
            } catch {
              // ignore
            }
            if (facetsFormComponent && typeof /** @type {any} */ (facetsFormComponent).updateFilters === 'function') {
              await /** @type {any} */ (facetsFormComponent).updateFilters();
            }
          }
        }
      }
    } catch (error) {
      console.warn('[THUYSVERS-SURVEY] Failed to hydrate from subscriptionsurvey_email param', error);
    } finally {
      if (loaderShown) {
        try {
          LoadManager.removeLoader();
        } catch {
          // ignore
        }
      }
    }
  };

  disconnectedCallback() {
    // Remove listeners we added in connectedCallback
    this.refs.closeSurveyButton?.removeEventListener('click', this.#closeSurveyForm);
    this.refs.startSurveyButton?.removeEventListener('click', this.#startSurvey);
    this.refs.prevSurveyButton?.removeEventListener('click', this.#prevSurveyStep);
    this.refs.nextSurveyButton?.removeEventListener('click', this.#nextSurveyStep);

    this.formEl?.removeEventListener('input', this.#onFormInputOrChange, true);
    this.formEl?.removeEventListener('change', this.#onFormInputOrChange, true);
    this.formEl?.removeEventListener('keydown', this.#onFormKeyDown, true);
    this.formEl = null;

    // Remove qty listeners we bound in bindQtyControlsStatic
    for (const [btn, handler] of this.qtyBtnHandlers.entries()) {
      btn.removeEventListener('click', handler);
    }
    this.qtyBtnHandlers.clear();

    for (const [input, handler] of this.qtyInputHandlers.entries()) {
      input.removeEventListener('input', handler);
      input.removeEventListener('change', handler);
    }
    this.qtyInputHandlers.clear();

    if (this.saveStateTimer) {
      window.clearTimeout(this.saveStateTimer);
      this.saveStateTimer = null;
    }

    super.disconnectedCallback();
  }

  // ----- Session state (restore/persist) -----

  /** Returns the sessionStorage key for in-progress form state. */
  getSurveySessionStateKey() {
    // Generic key since there's only 1 form and the form id may vary across pages/history states.
    return STORAGE_KEY_SUBSCRIPTION_SURVEY_STATE;
  }

  /** Captures openSurvey from URL into sessionStorage (URL is often cleaned shortly after load). */
  #captureOpenSurveyIntent() {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = String(params.get('openSurvey') || '').trim().toLowerCase();
      if (raw === 'true' || raw === '1' || raw === 'yes') {
        sessionStorage.setItem(STORAGE_KEY_OPEN_SURVEY_INTENT, '1');
      }
    } catch {
      // ignore
    }
  }

  /** True when URL has ?openSurvey=true|1|yes or we captured that intent before URL was cleaned. */
  isOpenSurveyParamEnabled() {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = String(params.get('openSurvey') || '').trim().toLowerCase();
      if (raw === 'true' || raw === '1' || raw === 'yes') return true;
      return sessionStorage.getItem(STORAGE_KEY_OPEN_SURVEY_INTENT) === '1';
    } catch {
      return false;
    }
  }

  /** Clears the captured openSurvey intent after we've used it. */
  #clearOpenSurveyIntent() {
    try {
      sessionStorage.removeItem(STORAGE_KEY_OPEN_SURVEY_INTENT);
    } catch {
      // ignore
    }
  }

  /**
   * Reads email from subscription_survey_result in sessionStorage (submitter.email).
   * Used when openSurvey=true for "take survey again" flow - user has already taken the survey.
   * @returns {string}
   */
  getEmailFromSubscriptionSurveyResult() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_SUBSCRIPTION_SURVEY_RESULT);
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return '';
      const submitter = parsed.submitter;
      if (!submitter || typeof submitter !== 'object') return '';
      const email = String(submitter.email || '').trim();
      return email && EMAIL_REGEX.test(email) ? email : '';
    } catch {
      return '';
    }
  }

  /**
   * Prefills the email input from subscription_survey_result when openSurvey=true (retake flow).
   * Call after restoreSurveySessionState so we overlay the known-good email from the previous submission.
   */
  prefillEmailFromSubscriptionSurveyResult() {
    const email = this.getEmailFromSubscriptionSurveyResult();
    if (!email) return;
    const form = this.formEl;
    if (!form) return;
    const emailInput = form.querySelector('input[type="email"]');
    if (emailInput instanceof HTMLInputElement) {
      emailInput.value = email;
      this.queueSaveSurveySessionState();
    }
  }

  /**
   * On page load:
   * - If the user has recommendations and a complete subscription_survey_result (non-empty
   *   subscriptionFrequency), dispatch "subscription-survey-completed" so the start modal can open.
   * - Otherwise, open the survey form immediately and restore any in-progress state.
   */
  routeInitialSurveyState() {
    try {
      // 0) If URL explicitly requests opening the survey, honor that first.
      if (this.isOpenSurveyParamEnabled()) {
        this.showSurveyForm();
        const retakeEmail = this.getEmailFromSubscriptionSurveyResult();
        if (retakeEmail) {
          // Retake flow: skip email step (before restore so step routing is correct)
          this.setEmailStepEnabled(false);
        }
        this.restoreSurveySessionState();
        if (retakeEmail) {
          this.prefillEmailFromSubscriptionSurveyResult();
          // Ensure we show the first visible step (profile count), not the now-skipped email step
          const firstVisible = this.findNextVisibleStepIndex(-1, 1);
          if (firstVisible >= 0) {
            this.setActiveStepIndex(firstVisible);
            this.updateNavState();
          }
        }
        return;
      }

      // 1) If logged-in with mijn_gegevens metafield, persist to session first.
      if (this.hasLoggedInMijnGegevens()) {
        this.#persistMijnGegevensToSessionStorage();
      }

      // 2) Skip survey only when recommendations exist and subscription_survey_result is complete
      //    (includes non-empty subscriptionFrequency — same rule as cart / selling-plan gate).
      const hasRecommendations = sessionStorage.getItem(STORAGE_KEY_RECOMMENDATIONS);

      if (hasRecommendations && hasValidSubscriptionSurveyResultForSession()) {
        this.#closeSurveyForm();
        const source = this.hasLoggedInMijnGegevens() ? 'mijn-gegevens-metafield' : 'session-storage';
        window.dispatchEvent(new CustomEvent('subscription-survey-completed', { detail: { source } }));
        window.dispatchEvent(new Event(EVENT_RECOMMENDATIONS_UPDATED));
        return;
      }
      
      // No result found: open the survey and restore any saved in-progress state.
      this.showSurveyForm();
      this.restoreSurveySessionState();
    } catch {
      // ignore
    }
  }

  /**
   * @returns {boolean}
   * True if the customer is logged in and has the "mijn_gegevens" customer metafield filled.
   * In that case the survey should be skipped.
   */
  hasLoggedInMijnGegevens() {
    return this.getAttribute('data-has-mijn-gegevens') === 'true';
  }

  /**
   * Reads mijn_gegevens and subscription_survey_result from embedded JSON and persists
   * them to "recommendations" and "subscription_survey_result" sessionStorage keys.
   */
  #persistMijnGegevensToSessionStorage() {
    try {
      const mijnGegevensEl =
        this.querySelector('[data-survey-mijn-gegevens-json]') ||
        document.querySelector('[data-ecomm-mijn-gegevens-json]');
      if (mijnGegevensEl instanceof HTMLElement) {
        const raw = String(mijnGegevensEl.textContent || '').trim();
        if (raw) {
          const payload = JSON.parse(raw);
          if (payload && typeof payload === 'object') this.persistRecommendationsPayload(payload);
        }
      }
      const surveyResultEl = this.querySelector('[data-survey-subscription-survey-result-json]');
      if (surveyResultEl instanceof HTMLElement) {
        const raw = String(surveyResultEl.textContent || '').trim();
        if (raw) {
          const payload = JSON.parse(raw);
          if (payload && typeof payload === 'object') this.persistSubscriptionSurveyResult(payload);
        }
      }
    } catch {
      console.log('[THUYSVERS-SURVEY] Error persisting mijn_gegevens to sessionStorage');
    }
  }

  /**
   * Throttled state-save to sessionStorage.
   * Use this for input/change events to avoid excessive writes.
   */
  queueSaveSurveySessionState() {
    if (this.saveStateTimer) window.clearTimeout(this.saveStateTimer);
    this.saveStateTimer = window.setTimeout(() => {
      this.saveStateTimer = null;
      this.saveSurveySessionState();
    }, 150);
  }

  /** Removes saved in-progress state from sessionStorage. */
  clearSurveySessionState() {
    try {
      sessionStorage.removeItem(this.getSurveySessionStateKey());
    } catch {
      // ignore
    }
  }

  /**
   * Serializes form values (radios, checkboxes, inputs, selects) and active step into JSON.
   * Two-pass: first init radio/checkbox groups, second collect values.
   * @returns {{ v: 1; updatedAt: string; activeStepHandle: string; activeStepIndex: number; values: Record<string, string | string[]> } | null}
   */
  serializeSurveySessionState() {
    const form = this.formEl;
    if (!form) return null;

    /** @type {Record<string, string | string[]>} */
    const values = {};

    /** @type {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]} */
    const fields = Array.from(form.querySelectorAll('input[name], select[name], textarea[name]')).filter(
      (el) => el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
    );

    // First pass: initialize checkbox/radio groups.
    for (const el of fields) {
      const name = String(el.name || '').trim();
      if (!name) continue;

      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        if (!Array.isArray(values[name])) values[name] = [];
        continue;
      }

      if (el instanceof HTMLInputElement && el.type === 'radio') {
        // We'll fill the selected value in the second pass; ensure key exists.
        if (typeof values[name] !== 'string') values[name] = '';
        continue;
      }
    }

    // Second pass: collect values.
    for (const el of fields) {
      const name = String(el.name || '').trim();
      if (!name) continue;

      if (el instanceof HTMLInputElement) {
        if (el.type === 'radio') {
          if (el.checked) values[name] = String(el.value ?? '');
          continue;
        }

        if (el.type === 'checkbox') {
          const arr = Array.isArray(values[name]) ? /** @type {string[]} */ (values[name]) : [];
          if (el.checked) arr.push(String(el.value ?? 'on'));
          values[name] = arr;
          continue;
        }

        values[name] = String(el.value ?? '');
        continue;
      }

      if (el instanceof HTMLSelectElement) {
        if (el.multiple) {
          values[name] = Array.from(el.selectedOptions).map((o) => String(o.value ?? ''));
        } else {
          values[name] = String(el.value ?? '');
        }
        continue;
      }

      if (el instanceof HTMLTextAreaElement) {
        values[name] = String(el.value ?? '');
      }
    }

    const activeStep = this.getActiveStep();
    const activeStepHandle = String(activeStep?.dataset?.handle || '').trim();
    const activeStepIndex = this.getActiveStepIndex();

    return {
      v: 1,
      updatedAt: new Date().toISOString(),
      activeStepHandle,
      activeStepIndex,
      values,
    };
  }

  /** Persists current form state to sessionStorage (throttled via queueSaveSurveySessionState). */
  saveSurveySessionState() {
    const state = this.serializeSurveySessionState();
    if (!state) return;
    try {
      sessionStorage.setItem(this.getSurveySessionStateKey(), JSON.stringify(state));
    } catch (error) {
      console.warn('[THUYSVERS-SURVEY] Failed to persist survey session state', error);
    }
  }

  /**
   * Restores serialized values into form controls (radios, checkboxes, selects, inputs).
   * @param {Record<string, string | string[]>} values
   */
  applySurveySessionValues(values) {
    const form = this.formEl;
    if (!form) return;

    /** @type {Map<string, (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]>} */
    const byName = new Map();
    const fields = Array.from(form.querySelectorAll('input[name], select[name], textarea[name]')).filter(
      (el) => el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
    );
    for (const el of fields) {
      const name = String(el.name || '').trim();
      if (!name) continue;
      const arr = byName.get(name) || [];
      arr.push(el);
      byName.set(name, arr);
    }

    for (const [name, raw] of Object.entries(values || {})) {
      const controls = byName.get(name);
      if (!controls || controls.length === 0) continue;

      const first = controls[0];

      // Radios
      if (first instanceof HTMLInputElement && first.type === 'radio') {
        const selected = String(raw ?? '');
        for (const c of controls) {
          if (c instanceof HTMLInputElement && c.type === 'radio') {
            c.checked = selected !== '' && String(c.value ?? '') === selected;
          }
        }
        continue;
      }

      // Checkboxes
      if (first instanceof HTMLInputElement && first.type === 'checkbox') {
        const selectedSet = new Set(Array.isArray(raw) ? raw.map(String) : []);
        for (const c of controls) {
          if (c instanceof HTMLInputElement && c.type === 'checkbox') {
            const v = String(c.value ?? 'on');
            c.checked = selectedSet.has(v);
          }
        }
        continue;
      }

      // Select multiple
      if (first instanceof HTMLSelectElement && first.multiple) {
        const selectedSet = new Set(Array.isArray(raw) ? raw.map(String) : []);
        for (const c of controls) {
          if (c instanceof HTMLSelectElement) {
            for (const opt of Array.from(c.options)) {
              opt.selected = selectedSet.has(String(opt.value ?? ''));
            }
          }
        }
        continue;
      }

      // Default: set value on the first control (text/number/select/textarea)
      const valueStr = Array.isArray(raw) ? (raw[0] ? String(raw[0]) : '') : String(raw ?? '');
      if (first instanceof HTMLInputElement || first instanceof HTMLSelectElement || first instanceof HTMLTextAreaElement) {
        first.value = valueStr;
      }
    }
  }

  /** @param {string} handle Finds step index by data-handle attribute. @returns {number} */
  getStepIndexByHandle(handle) {
    const h = String(handle || '').trim();
    if (!h) return -1;
    const steps = this.refs.surveyStep || [];
    return steps.findIndex((s) => String(s?.dataset?.handle || '').trim() === h);
  }

  /** @param {number} targetIndex When target step is skipped, returns nearest visible step. @returns {number} */
  findNearestVisibleStepIndex(targetIndex) {
    const steps = this.refs.surveyStep || [];
    if (targetIndex < 0 || targetIndex >= steps.length) return -1;
    if (!steps[targetIndex]?.hasAttribute('data-skipped')) return targetIndex;

    const forward = this.findNextVisibleStepIndex(targetIndex, 1);
    const backward = this.findNextVisibleStepIndex(targetIndex, -1);
    if (forward < 0) return backward;
    if (backward < 0) return forward;

    return (forward - targetIndex) <= (targetIndex - backward) ? forward : backward;
  }

  /** Reads saved state from sessionStorage and applies values + step position. */
  restoreSurveySessionState() {
    const form = this.formEl;
    if (!form) return;

    let parsed = null;
    try {
      const raw = sessionStorage.getItem(this.getSurveySessionStateKey());
      if (!raw) return;
      parsed = JSON.parse(raw);
    } catch {
      return;
    }

    if (!parsed || typeof parsed !== 'object') return;
    /** @type {any} */
    const state = parsed;
    if (state.v !== 1 || !state.values || typeof state.values !== 'object') return;

    this.applySurveySessionValues(/** @type {any} */ (state.values));

    // Re-apply person-step skipping based on restored profileCount
    this.profileCountSelected = this.getSelectedProfileCount();
    this.applyPersonStepSkipping(this.profileCountSelected);

    const byHandle = this.getStepIndexByHandle(String(state.activeStepHandle || ''));
    const byIndex = Number.isFinite(Number(state.activeStepIndex)) ? Number(state.activeStepIndex) : -1;
    let target = byHandle >= 0 ? byHandle : byIndex;

    target = this.findNearestVisibleStepIndex(target);
    if (target >= 0) {
      this.setActiveStepIndex(target);
    }

    this.updateNavState();
  }

  // ----- Navigation & steps -----

  getActiveStep() {
    return Utils.getActiveStep(this);
  }

  getActiveStepIndex() {
    return Utils.getActiveStepIndex(this);
  }

  /** @param {number} index */
  setActiveStepIndex(index) {
    return Utils.setActiveStepIndex(this, index);
  }

  updateNavState() {
    Utils.updateNavState(this, { isGuardCheck: Utils.isGuardStatusFieldType });
  }

  /** @param {HTMLInputElement} emailInput @returns {boolean} */
  syncEmailValidity(emailInput) {
    return Utils.syncEmailValidity(emailInput);
  }

  canAdvanceFromActiveStep() {
    return Utils.canAdvanceFromActiveStep(this);
  }

  /**
   * @param {Event} event
   */
  #onFormInputOrChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    // Only re-evaluate button state when changing inputs in the active step.
    const activeStep = this.getActiveStep();
    if (!activeStep) return;
    if (!activeStep.contains(target)) return;

    // Quantity is handled by dedicated listeners. Avoid any nav-state logic here.
    if (target instanceof HTMLInputElement && target.type === 'number' && target.classList.contains('thuysvers-survey-form-qty-input')) {
      this.queueSaveSurveySessionState();
      return;
    }

    this.updateNavState();
    this.queueSaveSurveySessionState();
  }

  // ----- API / Klaviyo -----

  getGadgetProgressEndpoint() {
    return Utils.getGadgetProgressEndpoint(this);
  }

  getGadgetStatusEndpoint() {
    return Utils.getGadgetStatusEndpoint(this);
  }

  getCustomerGidFromForm() {
    return Utils.getCustomerGidFromForm(this);
  }

  getSubmitterEmailFromForm() {
    return Utils.getSubmitterEmailFromForm(this);
  }

  /** @param {'started'|'completed'} status @param {string} email @param {Record<string, unknown>} [extra] */
  async sendSurveyProgress(status, email, extra) {
    return Klaviyo ? Klaviyo.sendSurveyProgress(this, status, email, extra) : Promise.resolve();
  }

  async maybeTrackSurveyStarted() {
    return Klaviyo ? Klaviyo.maybeTrackSurveyStarted(this, {
      storageKeyStarted: STORAGE_KEY_SURVEY_STARTED_EMAIL,
      storageKeyCompleted: STORAGE_KEY_SURVEY_COMPLETED_EMAIL,
      emailRegex: EMAIL_REGEX,
    }) : Promise.resolve();
  }

  /** @param {string} email */
  async fetchSurveyStatus(email) {
    return Utils.fetchSurveyStatus(this, email, { includeSubscriptionFields: true, emailRegex: EMAIL_REGEX });
  }

  getSurveyStatusStepIndex() {
    return Utils.getSurveyStatusStepIndex(this);
  }

  /** @param {boolean} enabled */
  setSurveyStatusStepEnabled(enabled) {
    return Utils.setSurveyStatusStepEnabled(this, enabled);
  }

  /**
   * Finds the index of the FirmhouseSubscriptionStatus guard step.
   * @returns {number}
   */
  getFirmhouseSubscriptionStatusStepIndex() {
    const steps = this.refs.surveyStep || [];
    return steps.findIndex((s) => String(s?.dataset?.fieldType || '') === FIELD_TYPE.FIRMHOUSE_SUBSCRIPTION);
  }

  /**
   * Enables/disables the FirmhouseSubscriptionStatus guard step in the navigation flow.
   * @param {boolean} enabled
   */
  setFirmhouseSubscriptionStatusStepEnabled(enabled) {
    const idx = this.getFirmhouseSubscriptionStatusStepIndex();
    const step = idx >= 0 ? this.refs.surveyStep?.[idx] : null;
    if (!step) return;
    if (enabled) step.removeAttribute('data-skipped');
    else step.setAttribute('data-skipped', '');
  }

  /**
   * Skips or includes the email step (used when openSurvey + subscription_survey_result = retake flow).
   * @param {boolean} enabled - false = skip email step
   */
  setEmailStepEnabled(enabled) {
    const steps = this.refs.surveyStep || [];
    const idx = steps.findIndex((s) => String(s?.dataset?.fieldType || '') === FIELD_TYPE.EMAIL);
    const step = idx >= 0 ? steps[idx] : null;
    if (!step) return;
    if (enabled) step.removeAttribute('data-skipped');
    else step.setAttribute('data-skipped', '');
  }

  // ----- Quantity controls -----

  bindQtyControlsStatic() {
    Utils.bindQtyControlsStatic(this, { onInteraction: () => this.queueSaveSurveySessionState() });
  }

  /** @param {HTMLInputElement} input @returns {number} */
  getQtyInputNumber(input) {
    return Utils.getQtyInputNumber(input);
  }

  /** @param {HTMLInputElement} input */
  reportQtyRangeValidity(input) {
    return Utils.reportQtyRangeValidity(input);
  }

  /** @param {HTMLInputElement} input @param {number} delta */
  bumpQtyInputValue(input, delta) {
    return Utils.bumpQtyInputValue(this, input, delta);
  }

  // ----- Step handlers (Prev/Next/Start) -----

  /**
   * Prevent "Enter" from submitting the form; instead try to advance to next step.
   * @param {KeyboardEvent} event
   */
  #onFormKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    // Allow default behavior inside textarea (not currently used, but safer).
    const target = event.target;
    if (target instanceof HTMLTextAreaElement) return;

    event.preventDefault();

    // Mirror the "Next" button behavior
    if (this.canAdvanceFromActiveStep()) {
      this.#nextSurveyStep();
      return;
    }

    // If blocked, show native validation UI (especially for email).
    const activeStep = this.getActiveStep();
    const emailInput = activeStep?.querySelector('input[type="email"]');
    if (emailInput instanceof HTMLInputElement) emailInput.reportValidity();
  }

  #startSurvey = () => {
    // When retaking (openSurvey + subscription_survey_result), skip to first step after email (profile count)
    if (this.isOpenSurveyParamEnabled() && this.getEmailFromSubscriptionSurveyResult()) {
      const firstSurveyStep = this.findNextVisibleStepIndex(0, 1);
      this.setActiveStepIndex(firstSurveyStep >= 0 ? firstSurveyStep : 1);
    } else {
      this.setActiveStepIndex(1);
    }
    this.updateNavState();
    this.saveSurveySessionState();
  }

  #prevSurveyStep = () => {
    const currentStep = this.getActiveStep();
    const currentIndex = this.getActiveStepIndex();
    if (!currentStep || currentIndex <= 0) return;

    // Special case: if we're on the add-ons step, jump back to the last step for the selected profileCount.
    const answerKey = String(currentStep.dataset?.answerKey || '').trim().toLowerCase();
    if (answerKey === ANSWER_KEY_ADDONS) {
      this.profileCountSelected = this.getSelectedProfileCount();
      this.applyPersonStepSkipping(this.profileCountSelected);

      const targetIndex = this.findLastPersonStepIndex(this.profileCountSelected);
      if (targetIndex >= 0) {
        this.setActiveStepIndex(targetIndex);
        this.updateNavState();
        this.saveSurveySessionState();
        return;
      }
    }

    const prevIndex = this.findNextVisibleStepIndex(currentIndex, -1);
    if (prevIndex < 0) return;
    this.setActiveStepIndex(prevIndex);
    this.updateNavState();
    this.saveSurveySessionState();
  }

  #nextSurveyStep = async () => {
    // Phase 0: Block if validation fails
    if (!this.canAdvanceFromActiveStep()) {
      const activeStep = this.getActiveStep();
      const emailInput = activeStep?.querySelector('input[type="email"]');
      if (emailInput instanceof HTMLInputElement) emailInput.reportValidity();
      return;
    }

    const activeStep = this.getActiveStep();

    // Phase 1: Guard steps (SurveyStatus, FirmhouseSubscriptionStatus) → close & redirect
    if (Utils.isGuardStatusFieldType(activeStep?.dataset?.fieldType)) {
      const isFirmhouseStep = String(activeStep?.dataset?.fieldType || '') === FIELD_TYPE.FIRMHOUSE_SUBSCRIPTION;
      if (isFirmhouseStep) {
        try {
          sessionStorage.setItem(STORAGE_KEY_ACTIVE_SUBSCRIPTION, '1');
        } catch {
          // ignore
        }
      }
      this.#clearOpenSurveyIntent();
      window.dispatchEvent(new CustomEvent('subscription-survey-completed'));
      this.formEl?.reset();
      this.setSurveyStatusStepEnabled(false);
      this.setFirmhouseSubscriptionStatusStepEnabled(false);
      this.setActiveStepIndex(0);
      this.updateNavState();
      this.clearSurveySessionState();
      this.#closeSurveyForm();
      const redirectUrl =
        isFirmhouseStep
          ? this.formEl?.getAttribute('data-firmhouse-redirect-url')
          : this.formEl?.getAttribute('data-redirect-url');
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      }
      return;
    }

    // Phase 2: Email step → check status API, show guard if taken, or track "started"
    if (activeStep?.dataset?.fieldType === FIELD_TYPE.EMAIL) {
      // Bypass fetchSurveyStatus when openSurvey=true + subscription_survey_result has email (retake flow).
      const storedEmail = this.getEmailFromSubscriptionSurveyResult();
      const formEmail = this.getSubmitterEmailFromForm();
      const shouldBypassRetake =
        this.isOpenSurveyParamEnabled() &&
        storedEmail &&
        formEmail &&
        EMAIL_REGEX.test(String(formEmail).trim());

      if (shouldBypassRetake) {
        await this.maybeTrackSurveyStarted();
        // Fall through to Phase 5 to advance to next survey step (skip status check).
      } else {
        // Prevent double clicks while we check.
        if (this.isCheckingStatus) return;
        this.isCheckingStatus = true;
        this.refs.nextSurveyButton && (this.refs.nextSurveyButton.disabled = true);

        try {
          const email = this.getSubmitterEmailFromForm();
          const status = await this.fetchSurveyStatus(email);

          // Persist any returned payload (helps recommendations/quick-filters) regardless of which guard we show.
          if (status.payload) this.persistRecommendationsPayload(status.payload);
          if (status.subscriptionSurveyResult) this.persistSubscriptionSurveyResult(status.subscriptionSurveyResult);

          // Active subscription takes priority: store flag for guest (same-session return), show Firmhouse step.
          if (status.hasActivatedFirmhouseSubscription && status.hasOngoingFirmhouseSubscription) {
            try {
              sessionStorage.setItem(STORAGE_KEY_ACTIVE_SUBSCRIPTION, '1');
            } catch {
              // ignore
            }
            this.setFirmhouseSubscriptionStatusStepEnabled(true);
            const idx = this.getFirmhouseSubscriptionStatusStepIndex();
            if (idx >= 0) {
              this.setActiveStepIndex(idx);
              this.updateNavState();
              this.saveSurveySessionState();
              return;
            }
          }
          if (status.hasTakenSurvey) {
            this.setSurveyStatusStepEnabled(true);
            const idx = this.getSurveyStatusStepIndex();
            if (idx >= 0) {
              this.setActiveStepIndex(idx);
              this.updateNavState();
              this.saveSurveySessionState();
              return;
            }
          } else {
            await this.maybeTrackSurveyStarted();
          }
        } finally {
          this.isCheckingStatus = false;
          this.refs.nextSurveyButton && (this.refs.nextSurveyButton.disabled = false);
        }
      }
    }

    // Phase 3: ProfileCount step → apply person-step skipping
    const activeAnswerKey = String(activeStep?.dataset?.answerKey || '').trim().toLowerCase();
    if (activeAnswerKey === ANSWER_KEY_PROFILE_COUNT) {
      this.profileCountSelected = this.getSelectedProfileCount();
      this.applyPersonStepSkipping(this.profileCountSelected);
    }

    const currentIndex = this.getActiveStepIndex();
    if (currentIndex < 0) return;

    // Phase 4: Submit step (from URL/back) → close & redirect (no async submit)
    if (activeStep?.dataset?.fieldType === FIELD_TYPE.SUBMIT) {
      // Allow the subscription start modal to show (it listens for this event and/or subscription_survey_result in sessionStorage)
      window.dispatchEvent(new CustomEvent('subscription-survey-completed'));
      this.formEl?.reset();
      this.setActiveStepIndex(0);
      this.updateNavState();
      this.clearSurveySessionState();
      this.#closeSurveyForm();
      const redirectUrl = this.formEl?.getAttribute('data-redirect-url');
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      }
      return;
    }

    // Phase 5: Advance to next step
    const nextIndex = this.findNextVisibleStepIndex(currentIndex, 1);
    const nextStep = nextIndex >= 0 ? this.refs.surveyStep?.[nextIndex] : null;
    if (!nextStep) return;

    this.setActiveStepIndex(nextIndex);

    // Phase 6: If next step is Submit → run async submit flow (loader, Gadget API, Klaviyo)
    if (nextStep.dataset?.fieldType === FIELD_TYPE.SUBMIT) {
      const payload = /** @type {Record<string, unknown>} */ (
        Utils.buildSubmissionJson(this, { useExtendedEmail: true, includeSubscriptionFrequency: true })
      );
      Utils.runSubmitStepAsync(this, payload, {
        /** @param {string} email @param {Record<string, unknown>} [extra] */
        sendProgress: Klaviyo ? (email, extra) => void Klaviyo.sendSurveyProgress(this, 'completed', email, extra) : undefined,
        /** @param {Record<string, unknown>} p @param {() => string} getForm */
        getCompletedEmail: (p, getForm) => {
          const emailFromPayload = p?.submitter && typeof p.submitter === 'object' && typeof /** @type {{ email?: string }} */ (p.submitter).email === 'string'
            ? String(/** @type {{ email?: string }} */ (p.submitter).email).trim()
            : '';
          const emailFromForm = getForm();
          const emailFromStarted = (() => {
            try {
              return sessionStorage.getItem(STORAGE_KEY_SURVEY_STARTED_EMAIL) || '';
            } catch {
              return '';
            }
          })();
          return (emailFromPayload && EMAIL_REGEX.test(emailFromPayload))
            ? emailFromPayload
            : (emailFromForm && EMAIL_REGEX.test(emailFromForm))
              ? emailFromForm
              : (emailFromStarted && EMAIL_REGEX.test(emailFromStarted))
                ? emailFromStarted
                : '';
        },
        storageKeyCompleted: STORAGE_KEY_SURVEY_COMPLETED_EMAIL,
        emailRegex: EMAIL_REGEX,
      });
    }

    this.updateNavState();
    this.saveSurveySessionState();
  }

  /** @param {number | undefined} status @returns {string} */
  getSubmitErrorMessage(status) {
    return Utils.getSubmitErrorMessage(status);
  }

  /** @param {unknown} payload @returns {Promise<{ ok: boolean; status?: number }>} */
  async submitToGadgetEndpoint(payload) {
    return Utils.submitToGadgetEndpoint(this, payload, {
      /** @param {unknown} p */
      onSuccessPersist: (p) => this.persistSubscriptionSurveyResult(p),
    });
  }

  /** @param {unknown} payload */
  persistRecommendationsPayload(payload) {
    return Utils.persistRecommendationsPayload(payload);
  }

  /**
   * Stores the subscription survey submission result in sessionStorage under "subscription_survey_result".
   * @param {any} payload
   */
  persistSubscriptionSurveyResult(payload) {
    try {
      sessionStorage.setItem(STORAGE_KEY_SUBSCRIPTION_SURVEY_RESULT, JSON.stringify(payload || {}));
      window.dispatchEvent(new Event(EVENT_RECOMMENDATIONS_UPDATED));
    } catch (error) {
      console.warn('[THUYSVERS-SURVEY] Failed to persist subscription survey result to sessionStorage', error);
    }
  }

  /** @param {unknown} payload */
  persistChurnedRecommendationsPayload(payload) {
    return Utils.persistChurnedRecommendationsPayload(payload);
  }

  // ----- Utils wrappers -----

  getSelectedProfileCount() {
    return Utils.getSelectedProfileCount(this);
  }

  /** @param {number} profileCount */
  applyPersonStepSkipping(profileCount) {
    return Utils.applyPersonStepSkipping(this, profileCount);
  }

  /** @param {number} fromIndex @param {1 | -1} direction @returns {number} */
  findNextVisibleStepIndex(fromIndex, direction) {
    return Utils.findNextVisibleStepIndex(this, fromIndex, direction);
  }

  /** @param {number} profileCount @returns {number} */
  findLastPersonStepIndex(profileCount) {
    return Utils.findLastPersonStepIndex(this, profileCount);
  }

  /** @param {HTMLFormElement} form @returns {unknown[]} */
  collectAddOns(form) {
    return Utils.collectAddOns(this, form);
  }

  /** @param {string} raw @returns {{ size: string, unit: string }} */
  parseSizeUnit(raw) {
    return Utils.parseSizeUnit(raw);
  }

  showSurveyForm = () => {
    Utils.showSurveyForm(this);
  };

  #closeSurveyForm = () => {
    this.#clearOpenSurveyIntent();
    Utils.closeSurveyForm(this);
  };

}

if (!customElements.get('thuysvers-subscription-survey-component')) {
  customElements.define('thuysvers-subscription-survey-component', ThuysversSubscriptionSurveyComponent);
}